'use client';

import { Button } from '@shared/components/shadcn';
import { LottolandSmileLogo, StarFilled } from '@shared/icons';
import { Verified } from '@shared/icons/system';
import { ROUTES, getBrand, isKenoGoBrand, searchParamsBuilder, setAwaitingRegistrationSuccessRedirect, useClientConfig, type Brand } from '@shared/utils';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMultistepForm } from '../../form-controls/MultistepForm/hooks/useMultistepForm';
import { getRegistrationContext } from '../../Forms/Registration/stepsConfig';

type SuccessStepProps = {
  title: string | React.ReactNode | ((firstName: string) => React.ReactNode);
  description: string | React.ReactNode;
  buttonText?: string;
  onContinue?: () => void;
  firstName?: string;
  extractFirstNameFromForm?: boolean;
  testId?: string;
  trackName?: string;
  redirectUrl?: string;
  trackContainer?: string;
  trackFeature?: string;
  additionalTrackingProps?: Record<string, string>;
  brandOverride?: Brand;
};

const BLING_POSITIONS = [
  { top: '-10px', left: '60px', delay: '0.2s' },
  { top: '50px', left: '-10px', delay: '0.3s' },
];

function StarBling({ delay, style }: { delay: string; style?: React.CSSProperties }) {
  return (
    <div
      className="absolute bling-animate pointer-events-none"
      style={{
        animationDelay: delay,
        ...style,
      }}
    >
      <StarFilled size="tiny" className="text-foreground" />
    </div>
  );
}

function LogoWithBlings({ isKenoGo }: { isKenoGo: boolean }) {
  if (isKenoGo) {
    return <Verified className="shrink-0" data-testid="kenogo-success-icon" size="extra-extra-large" />;
  }

  return (
    <div className="relative shrink-0 size-12" data-name="logo box">
      <div className="absolute inset-0 bg-accent-green rounded-lg flex items-center justify-center">
        <LottolandSmileLogo size="regular" className="text-secondary" />
      </div>

      {/* Star blings around the logo - positioned directly like in Figma */}
      {BLING_POSITIONS.map((pos, index) => (
        <StarBling
          key={index}
          delay={pos.delay}
          style={{
            top: pos.top,
            left: pos.left,
          }}
        />
      ))}
    </div>
  );
}

type SuccessContentProps = {
  title: string | React.ReactNode;
  description: string | React.ReactNode;
};

function SuccessContent({ title, description, isKenoGo }: SuccessContentProps & { isKenoGo: boolean }) {
  return (
    <div className="flex flex-col gap-4 items-center justify-center w-full max-w-[295px] mt-6">
      <h4 className={`text-foreground relative shrink-0 w-full ${isKenoGo ? 'heading-4' : "display-s font-normal [font-variation-settings:'wdth'_0]"}`} data-testid="success-title">
        {title}
      </h4>

      <div
        className={`paragraph relative shrink-0 w-full ${isKenoGo ? 'text-foreground-subtle' : 'text-foreground'}`}
        style={isKenoGo ? undefined : { fontVariationSettings: "'wdth' 0" }}
      >
        {description}
      </div>
    </div>
  );
}

function SuccessStep({
  title,
  description,
  buttonText,
  onContinue,
  firstName,
  extractFirstNameFromForm = false,
  testId = 'success-button',
  trackName = 'Continue',
  redirectUrl,
  trackContainer = 'Success',
  trackFeature,
  additionalTrackingProps = {},
  brandOverride,
}: SuccessStepProps) {
  const { goToNextStep } = useMultistepForm();
  const form = useFormContext();
  const config = useClientConfig();
  const router = useRouter();
  const brand = getBrand(brandOverride);
  const isKenoGo = isKenoGoBrand(brand);

  // Extract firstName from form if requested and not provided as prop
  const resolvedFirstName = firstName || (extractFirstNameFromForm ? form?.getValues()?.personalDetails?.firstName : '') || '';

  // Go to the deposit step after 2 seconds
  useEffect(() => {
    const buildSuccessRedirectUrl = (url: string, code?: string | null): string => {
      // Handle relative URLs by prepending the current origin
      const fullUrl = url.startsWith('/') ? `${window.location.origin}${url}` : url;
      const builder = searchParamsBuilder(fullUrl);
      builder.setParam('registration-success', 'true');

      // Add code parameter if provided
      if (code) {
        builder.setParam('code', code);
      }

      return builder.toString();
    };

    const isInternalUrl = (url: string): boolean => {
      try {
        const urlObj = new URL(url, window.location.origin);
        return urlObj.pathname.startsWith('/') && urlObj.hostname === window.location.hostname;
      } catch {
        return url.startsWith('/');
      }
    };

    const redirectToUrl = (url: string) => {
      setAwaitingRegistrationSuccessRedirect(false);

      if (isInternalUrl(url)) {
        // Extract pathname and search params for Next.js router (it expects relative paths)
        try {
          const urlObj = new URL(url);
          const relativePath = urlObj.pathname + urlObj.search;
          router.push(relativePath);
        } catch {
          // Fallback: if URL parsing fails, try using it as-is for relative paths
          router.push(url);
        }
      } else {
        window.location.href = url;
      }
    };

    const buildObcodeRedirectUrl = (obcode: string, allParams: Record<string, string>): string => {
      const builder = searchParamsBuilder(`${config.register.redirectUrl}/ob`).setParam('code', obcode).setParam('registration-success', 'true');

      Object.entries(allParams).forEach(([key, value]) => {
        if (key.toLowerCase() !== 'code' && key.toLowerCase() !== 'obcode') {
          builder.setParam(key, value);
        }
      });

      return builder.toString();
    };

    const shouldUseHistoryBack = (referrer: string, targetUrl: string): boolean => {
      try {
        const referrerUrl = new URL(referrer);
        const targetUrlObj = new URL(targetUrl);
        const referrerDomain = referrerUrl.hostname.replace(/^www\./, '');
        const targetDomain = targetUrlObj.hostname.replace(/^www\./, '');

        return referrerDomain === targetDomain && typeof window !== 'undefined' && window.history.length > 1;
      } catch {
        return false;
      }
    };

    const performRedirect = () => {
      try {
        const registrationContext = getRegistrationContext();
        const finalRedirectUrl = redirectUrl || config.register.redirectUrl || ROUTES.FALLBACK_URL;

        if (!registrationContext) {
          return redirectToUrl(buildSuccessRedirectUrl(finalRedirectUrl));
        }

        const { obcode, referrer, allParams } = registrationContext;

        // For internal URLs
        if (isInternalUrl(finalRedirectUrl)) {
          const urlWithParams = buildSuccessRedirectUrl(finalRedirectUrl, obcode || null);
          return redirectToUrl(urlWithParams);
        }

        // For external URLs with obcode: use redirectUrl as base (add code + registration-success)
        // when redirectUrl points to base; use buildObcodeRedirectUrl when redirecting to /ob
        if (obcode) {
          try {
            const redirectUrlObj = new URL(finalRedirectUrl, window.location.origin);
            const isRedirectToObPage = redirectUrlObj.pathname.endsWith('/ob');
            if (isRedirectToObPage) {
              return redirectToUrl(buildObcodeRedirectUrl(obcode, allParams));
            }
          } catch {
            // Fall through to buildSuccessRedirectUrl for invalid URLs
          }
          return redirectToUrl(buildSuccessRedirectUrl(finalRedirectUrl, obcode));
        }

        if (referrer && shouldUseHistoryBack(referrer, finalRedirectUrl)) {
          setAwaitingRegistrationSuccessRedirect(false);
          return window.history.back();
        }

        redirectToUrl(buildSuccessRedirectUrl(finalRedirectUrl));
      } catch {
        redirectToUrl(ROUTES.FALLBACK_URL);
      }
    };

    if (!redirectUrl && !getRegistrationContext()) {
      return;
    }

    const timer = setTimeout(performRedirect, 2000);
    return () => clearTimeout(timer);
  }, [redirectUrl, form, config.baseUrl, config.register?.redirectUrl, router]);

  const handleOnClick = () => (onContinue ? onContinue() : goToNextStep());

  const resolvedTitle: string | React.ReactNode = typeof title === 'function' ? (resolvedFirstName ? title(resolvedFirstName) : '') : title;

  return (
    <div
      className="w-full flex flex-col min-h-[100dvh] self-center text-center items-center justify-center p-6 pt-10 md:p-10 md:max-w-2xl"
      data-track-container={trackContainer}
      {...(trackFeature && { 'data-track-feature': trackFeature })}
      {...additionalTrackingProps}
      data-testid="success-step"
    >
      <LogoWithBlings isKenoGo={isKenoGo} />

      <SuccessContent title={resolvedTitle} description={description} isKenoGo={isKenoGo} />

      {/* Action button - only show if buttonText is provided */}
      {buttonText && (
        <Button onClick={handleOnClick} className="w-full md:w-auto mt-6" data-testid={testId} data-track-name={trackName}>
          {buttonText}
        </Button>
      )}
    </div>
  );
}

export { SuccessStep };
